import { dragHandleSvg, deletionDragHandleSvg } from '@/images/dragHandles'
import { helpers, theme } from '@/plugins/theme'
import colors from 'vuetify/lib/util/colors'
import { PolygonStyle } from '@/utils/map/MapHelpers.js'
import { MapLegend, MapLegendItem } from '@/utils/map/Legend.js'
import { MAX_ACRES } from '@/utils/rules'
import i18n from '@/i18n'
import { numberWithCommas } from '@/utils/NumericMutations.js'

export const MAX_VERTICES = 500

export const DrawingMode = {
  Idle: {
    id: 0,
    label: 'pan',
    azMapsLabel: 'idle',
    icon: 'mdi-cursor-default'
  },
  DrawPolygon: {
    id: 1,
    label: 'drawPolygon',
    azMapsLabel: 'draw-polygon',
    icon: 'mdi-vector-polygon'
  },
  DrawRectangle: {
    id: 2,
    label: 'drawRectangle',
    azMapsLabel: 'draw-rectangle',
    icon: 'mdi-vector-square'
  },
  EditGeometry: {
    id: 3,
    label: 'editGeometry',
    azMapsLabel: 'edit-geometry',
    icon: 'mdi-pencil'
  },
  EraseGeometry: {
    id: 4,
    label: 'eraseShape',
    azMapsLabel: 'erase-geometry',
    icon: 'mdi-eraser'
  }
}

export const azureMapsDrawingEvents =
  ['drawingchanged',
    'drawingchanging',
    'drawingcomplete',
    'drawingmodechanged',
    'drawingstarted',
    'drawingerased']

// Polygon Styling
export const PolygonStatus = {
  None: {
    id: 0,
    fill: PolygonStyle.Cut.fill,
    stroke: PolygonStyle.Cut.stroke
  },
  Focused: {
    id: 1,
    fill: theme.primary,
    stroke: theme.primary
  },
  Focusable: {
    id: 2,
    fill: PolygonStyle.Cut.fill,
    stroke: PolygonStyle.Cut.fill
  },
  Erasable: {
    id: 3,
    fill: colors.yellow.accent3,
    stroke: colors.yellow.accent3
  },
  Invalid: {
    id: 4,
    fill: colors.red.accent3,
    stroke: colors.red.accent3
  }
}

const OPAQUE = 1
const TRANSLUCENT = 0.6
const handleOpaque = dragHandleSvg(OPAQUE)
const handleTranslucent = dragHandleSvg(TRANSLUCENT)
const handleNone = '<div></div>'
const handleStyle = (
  primary,
  secondary) => (
  {
    dragHandleStyle: {
      htmlContent: primary
    },
    secondaryDragHandleStyle: {
      htmlContent: secondary
    }
  })

export const dragHandleStyles = {
  default: handleStyle(
    handleOpaque,
    handleTranslucent
  ),
  deletion: handleStyle(
    deletionDragHandleSvg,
    handleNone
  ),
  none: handleStyle(
    handleNone,
    handleNone
  )
}

class DrawingMapLegend extends MapLegend {
  constructor () {
    super()
    this.hasFocusedShape = false
    this.hasInvalidShape = false
    this.currentDrawingMode = null
    this.createInvalid()
    this.createFocused()
  }

  resetCutStyle () {
    this.Cut.border = `2px solid ${PolygonStyle.Cut.stroke}`
    this.Cut.color = helpers.getTranslucent(PolygonStyle.Cut.legendFillOpacity * 100, PolygonStyle.Cut.fill)
  }

  createInvalid () {
    this.Invalid = new MapLegendItem('invalid', {
      border: `2px solid ${PolygonStatus.Invalid.stroke}`,
      color: helpers.getTranslucent(PolygonStyle.Cut.legendFillOpacity * 100, PolygonStatus.Invalid.fill)
    })
  }

  createFocused () {
    this.Focused = new MapLegendItem('focused', {
      border: `2px solid ${PolygonStatus.Focused.stroke}`,
      color: helpers.getTranslucent(PolygonStyle.Cut.legendFillOpacity * 100, PolygonStatus.Focused.fill)
    })
  }

  styleCut () {
    if (this.isEditMode) {
      if (this.hasFocusedShape) {
        this.Cut.border = `2px solid ${PolygonStatus.None.stroke}`
        this.Cut.color = helpers.getTranslucent(PolygonStyle.Cut.legendFillOpacity * 100, PolygonStyle.Cut.fill)
      } else {
        this.Cut.border = `2px solid ${PolygonStatus.Focusable.stroke}`
        this.Cut.color = helpers.getTranslucent(PolygonStyle.Cut.legendFillOpacity * 100, PolygonStatus.Focusable.fill)
      }
    } else if (this.isEraseMode) {
      this.Cut.border = `2px solid ${PolygonStatus.Erasable.stroke}`
      this.Cut.color = helpers.getTranslucent(PolygonStyle.Cut.legendFillOpacity * 100, PolygonStatus.Erasable.fill)
    } else {
      this.resetCutStyle()
    }
  }

  get content () {
    const content = super.content
    this.styleCut()
    if (this.hasFocusedShape) content.push(this.Focused)
    if (this.hasInvalidShape) content.push(this.Invalid)
    return content
  }

  get isEraseMode () { return this.currentDrawingMode?.id === DrawingMode.EraseGeometry.id }
  get isEditMode () { return this.currentDrawingMode?.id === DrawingMode.EditGeometry.id }
}

export function getDrawingLegendInstance () {
  return new DrawingMapLegend()
}

export const instructions = (
  drawingMode,
  editModeOption,
  shapeSelected,
  multipleShapes
) => {
  const i = allInstructions
  switch (drawingMode.azMapsLabel) {
    case 'draw-polygon': return [
      i.ClickToAddPosition,
      i.DoubleClickToCompleteShape,
      i.Divider,
      i.FToAddPositionAtCursor,
      i.CToCompleteShape,
      i.EscToCancelDrawing
    ]

    case 'draw-rectangle': return [
      i.HoldClickToDrawRectangle,
      i.Divider,
      i.EscToCancelDrawing
    ]

    case 'erase-geometry': return [
      i.ClickToEraseShape
    ]

    case 'edit-geometry': {
      if (!shapeSelected) {
        return [
          i.ClickToSelectShape
        ]
      } else {
        const common = [
          multipleShapes ? i.ClickToSelectDifferentShape : undefined,
          i.Divider,
          i.EnterToDeselect
        ].filter(i => i)
        switch (editModeOption) {
          case 'dragVertices': return [
            i.HoldToMoveVertex,
            ...common
          ]

          case 'eraseVertices': return [
            i.ClickToEraseVertex,
            ...common
          ]

          case 'dragAndRotate': return [
            i.DragToMoveShape,
            i.DragToRotateShape,
            ...common
          ]
          default: return undefined
        }
      }
    }
    default: return undefined
  }
}
const allInstructions = {
  Divider: {
    isDivider: true
  },

  ClickToAddPosition: {
    icon: ['mdi-cursor-default-click'],
    label: 'click',
    description: 'addAPosition'
  },

  DoubleClickToCompleteShape: {
    icon: ['mdi-cursor-default', 'mdi-cursor-default'],
    label: 'doubleClick',
    description: 'completeAShape'
  },

  FToAddPositionAtCursor: {
    icon: ['mdi-alpha-f-box-outline'],
    description: 'addAPositionAtCursor'
  },

  CToCompleteShape: {
    icon: ['mdi-alpha-c-box-outline'],
    description: 'completeAShape'
  },

  EscToCancelDrawing: {
    icon: ['mdi-keyboard-esc'],
    description: 'cancelDrawing'
  },

  HoldClickToDrawRectangle: {
    icon: ['mdi-cursor-default-gesture'],
    label: 'holdClick',
    description: 'drawARectangle'
  },

  ClickToEraseShape: {
    icon: ['mdi-vector-square', 'mdi-cursor-default-click'],
    label: 'clickShape',
    description: 'eraseShape'
  },

  ClickToSelectShape: {
    icon: ['mdi-vector-square', 'mdi-cursor-default-click'],
    label: 'clickShape',
    description: 'selectAShape'
  },

  ClickToSelectDifferentShape: {
    icon: ['mdi-vector-square', 'mdi-cursor-default-click'],
    label: 'clickShape',
    description: 'selectDifferentShape'
  },

  EnterToDeselect: {
    icon: ['mdi-keyboard-return'],
    description: 'deselectShape'
  },

  HoldToMoveVertex: {
    icon: ['mdi-vector-point', 'mdi-cursor-default-gesture'],
    label: 'holdVertex',
    description: 'moveVertex'
  },

  ClickToEraseVertex: {
    icon: ['mdi-vector-point', 'mdi-cursor-default-click'],
    label: 'clickVertex',
    description: 'eraseVertex'
  },

  DragToMoveShape: {
    icon: ['mdi-vector-square', 'mdi-cursor-default-gesture'],
    label: 'dragShape',
    description: 'moveShape'
  },

  DragToRotateShape: {
    icon: ['mdi-reload', 'mdi-cursor-default-gesture'],
    label: 'dragRotateButton',
    description: 'rotateShape'
  }
}

const MapDrawingAlertType = {
  Error: {
    id: 0,
    label: 'error',
    color: 'error'
  },
  Warning: {
    id: 1,
    label: 'warning',
    color: 'warning'
  }
}

export const MapDrawingAlertFeature = {
  SimplifyButton: {
    id: 0,
    label: 'simplifyButton'
  }
}

export const MapDrawingAlert = {
  None: null,
  IntersectSelf: {
    id: 1,
    type: MapDrawingAlertType.Error,
    message: i18n.t('shapeIntersectingSelfError'),
    preventNewShapes: true,
    preventEditShapes: false,
    showInvalidInLegend: true,
    hideAcreCount: true
  },
  Overlap: {
    id: 2,
    type: MapDrawingAlertType.Error,
    message: i18n.t('shapesOverlappingError'),
    preventNewShapes: true,
    preventEditShapes: false,
    showInvalidInLegend: true,
    hideAcreCount: true
  },
  MarkerOutside: {
    id: 3,
    type: MapDrawingAlertType.Error,
    message: i18n.t('shapeMustContainTractMarker'),
    preventNewShapes: false,
    preventEditShapes: false,
    showInvalidInLegend: false,
    hideAcreCount: false
  },
  TooComplex: {
    id: 4,
    type: MapDrawingAlertType.Warning,
    message: i18n.t('shapeTooComplex', { max: MAX_VERTICES }),
    features: [MapDrawingAlertFeature.SimplifyButton],
    preventNewShapes: true,
    preventEditShapes: true,
    showInvalidInLegend: false,
    hideAcreCount: false
  },
  TooBig: {
    id: 5,
    type: MapDrawingAlertType.Error,
    message: i18n.t('mustNotExceedNAcres', { n: numberWithCommas(MAX_ACRES) }),
    preventNewShapes: true,
    preventEditShapes: false,
    showInvalidInLegend: false,
    hideAcreCount: false
  }
}
